import React, { memo } from 'react';
import type { CSSProperties, MouseEventHandler } from 'react';
import { Link } from 'react-router-dom';
import type { LinkProps } from 'react-router-dom';

import PropTypes from 'prop-types';

import { Glass } from '@samasante/liquid-glass';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import type { IconColor } from 'apps/auth/components/common/Icon/Icon';
import useIsScrolled from 'apps/auth/hooks/useIsScrolled';
import clsx from 'clsx';

import styles from './DiskButton.module.css';

export type DiskButtonVariant = 'primary' | 'secondary' | 'glass' | 'secondary_light' | 'tertiary' | 'tertiary_light' | 'error' | 'errorHint';
export type DiskButtonSize = 'small' | 'tight' | 'fit';

type DiskButtonType = 'tagA' | 'button';

const variantClass: Record<DiskButtonVariant, string | undefined> = {
    primary: styles.primary,
    secondary: styles.secondary,
    glass: styles.glass,
    secondary_light: styles.secondaryLight,
    tertiary: undefined,
    tertiary_light: styles.tertiaryLight,
    error: styles.error,
    errorHint: styles.errorHint,
};

const sizeClass: Record<DiskButtonSize, string | undefined> = {
    small: styles.small,
    tight: undefined,
    fit: styles.fit,
};

export interface DiskButtonProps {
    iconName?: string;
    iconColor?: IconColor;
    size?: DiskButtonSize;
    variant?: DiskButtonVariant;
    type?: DiskButtonType;
    raised?: boolean;
    disabled?: boolean;
    to?: LinkProps['to'];
    href?: string;
    onClick?: MouseEventHandler<HTMLElement>;
    onMouseDown?: MouseEventHandler<HTMLElement>;
    id?: string;
    title?: string;
    marginRight?: string;
    marginTop?: string;
    labelText?: string;
    dataTestId?: string;
}

const DiskButtonComponent = ({
    iconName = 'cross',
    iconColor = 'primary',
    size = 'tight',
    variant,
    type,
    raised,
    disabled,
    to,
    href,
    onClick,
    onMouseDown,
    id,
    title,
    marginRight,
    marginTop,
    labelText,
    dataTestId,
}: DiskButtonProps) => {
    const isScrolled = useIsScrolled();
    const computedRaised = raised ?? isScrolled;

    const commonProps = {
        'aria-label': labelText,
        className: clsx(styles.button, variant && variantClass[variant], sizeClass[size], computedRaised && styles.raised, disabled && styles.disabled),
        style: marginRight || marginTop ? ({ marginRight, marginTop } as CSSProperties) : undefined,
        'data-testid': dataTestId,
        onClick,
        onMouseDown,
        id,
        title,
    };
    const icon = <Icon name={iconName} color={iconColor} size={size === 'small' ? '16px' : '24px'} />;

    let element;
    if (type === 'tagA') {
        element = (
            <a {...commonProps} href={href}>
                {icon}
            </a>
        );
    } else if (type === 'button') {
        element = (
            <button {...commonProps} type="button" disabled={disabled}>
                {icon}
            </button>
        );
    } else {
        element = (
            <Link {...commonProps} to={to ?? '#'}>
                {icon}
            </Link>
        );
    }

    return variant === 'glass' ? <Glass className={styles.glassLens}>{element}</Glass> : element;
};

DiskButtonComponent.propTypes = {
    type: PropTypes.oneOf(['tagA', 'button']),
    variant: PropTypes.oneOf(['primary', 'secondary', 'glass', 'secondary_light', 'tertiary', 'tertiary_light', 'error', 'errorHint']),
    size: PropTypes.oneOf(['small', 'tight', 'fit']),
    raised: PropTypes.bool,
    disabled: PropTypes.bool,
    to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    href: PropTypes.string,
    onClick: PropTypes.func,
    iconName: PropTypes.string,
    iconColor: PropTypes.string,
    id: PropTypes.string,
    title: PropTypes.string,
    marginRight: PropTypes.string,
    marginTop: PropTypes.string,
    labelText: PropTypes.string,
    dataTestId: PropTypes.string,
    onMouseDown: PropTypes.func,
};

DiskButtonComponent.displayName = 'DiskButton';

export const DiskButton = memo(DiskButtonComponent);
